import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
    Box, Typography, IconButton, Button,
    TextField, CircularProgress, Stack,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Icon } from '@iconify/react';
import moment from 'moment';
import { clearRound } from '../../redux/slices/contactRound';
import { closeWhatsappConversationWithReason } from '../../redux/slices/whatsappV2';
import { callApi } from '../../utils/apiCall';
import AssignedToLead from '../../components/AssignedToLead';
import { compactChipStyle } from '../../styles/stepFormStyles';
import BookingPanel from './BookingPanel';

const RADIUS = '10px';

const REMINDER_PRESETS = [
    { label: '2 hours', key: '2hours', getDate: () => { const d = new Date(); d.setHours(d.getHours() + 2); return d; } },
    { label: 'Tomorrow 9am', key: 'nextday', getDate: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; } },
    { label: '1 week', key: 'week', getDate: () => { const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(9, 0, 0, 0); return d; } },
    { label: '2 weeks', key: 'twoweek', getDate: () => { const d = new Date(); d.setDate(d.getDate() + 14); d.setHours(9, 0, 0, 0); return d; } },
    { label: 'Custom', key: 'custom', getDate: null },
    { label: 'No reminder', key: 'none', getDate: null },
];

export default function ResolvePanel({ open, onClose, onConfirmClose, contactDetails, practiceId, taskId, onShareMessage }) {
    const dispatch = useDispatch();
    const whatsAppThread = useSelector((state) => state.whatsappV2?.whatsAppChatMetaData?.thread);

    const [selectedOutcome, setSelectedOutcome] = useState(null);
    const [notes, setNotes] = useState('');
    const [closeReason, setCloseReason] = useState('');
    const [calledConfirmed, setCalledConfirmed] = useState(null);
    const [selectedCallOptions, setSelectedCallOptions] = useState([]);
    const [callNote, setCallNote] = useState('');
    const [reminderPreset, setReminderPreset] = useState(null);
    const [customDate, setCustomDate] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveResult, setSaveResult] = useState(null); // null | 'success' | 'error'
    const [assignToState, setAssignToState] = useState([]);

    const whatsAppLeadDetails = useSelector((state) => state.whatsappV2?.whatsAppChatMetaData?.leadDetails);
    const leadDetails = whatsAppLeadDetails || contactDetails?.leadDetails;
    const leadId = leadDetails?.id;
    const treatmentId = leadDetails?.lead_treatments?.[0]?.id
        || leadDetails?.lead_treatment?.id
        || contactDetails?.leadDetails?.lead_treatments?.[0]?.id;

    useEffect(() => {
        if (!open) {
            dispatch(clearRound());
            setSelectedOutcome(null);
            setNotes('');
            setCloseReason('');
            setCalledConfirmed(null);
            setReminderPreset(null);
            setCustomDate(null);
            setIsSaving(false);
            setSaveResult(null);
            setAssignToState([]);
            setSelectedCallOptions([]);
            setCallNote('');
        }
    }, [open, leadId, treatmentId, practiceId, dispatch]);

    // ── Follow Up handler ──
    const handleFollowUp = async () => {
        if (!reminderPreset || (reminderPreset === 'custom' && !customDate) || isSaving) return;
        setIsSaving(true);
        try {
            const backendOutcome = calledConfirmed ? 'SPOKE_NOT_BOOKED' : 'UNABLE_TO_CONTACT';

            // Create note — the note path handles count increment via contacted_via
            if (leadId && practiceId) {
                const callInfo = calledConfirmed && callNote ? `Call: ${callNote}` : '';
                await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/notes/`, 'post', {
                    note: backendOutcome === 'SPOKE_NOT_BOOKED' ? 'FOLLOW_UP' : 'UNABLE_TO_CONTACT',
                    additional_notes: [callInfo, notes].filter(Boolean).join(' | ') || 'WhatsApp follow up',
                    contacted_via: calledConfirmed ? 'PHONE' : 'WHATSAPP',
                    lead: leadId,
                    ...(treatmentId ? { treatment: treatmentId } : {}),
                });
            }

            // 3. Create reminder (skip if "No reminder")
            if (reminderPreset !== 'none') {
                const preset = REMINDER_PRESETS.find(p => p.key === reminderPreset);
                const reminderDate = reminderPreset === 'custom' ? new Date(customDate) : preset?.getDate?.();
                const presetLabel = reminderPreset === 'custom' ? moment(customDate).format('DD MMM, HH:mm') : preset?.label;
                if (reminderDate && leadId && practiceId) {
                    await callApi(`/api/v1/practices/${practiceId}/reminders/`, 'post', {
                        method: calledConfirmed ? 'CALL' : 'WHATSAPP',
                        datetime: `${moment(reminderDate).format('DD-MM-YYYY HH:mm:ss')}.0Z`,
                        note: notes || `Follow up - ${presetLabel}`,
                        is_completed: false,
                        group: 'FOLLOW_UP',
                        lead: leadId,
                        lead_treatment: treatmentId || undefined,
                        assignees: assignToState?.map(el => el?.id).filter(Boolean) || [],
                    });
                }
            }

            // 3. Close WhatsApp thread
            const threadId = whatsAppThread?.id;
            if (threadId && leadId) {
                dispatch(closeWhatsappConversationWithReason(threadId, 'Follow Up', leadId));
            }

            // 4. Complete To-Do task if from To-Do
            if (taskId && practiceId) {
                await callApi(`/api/v1/practices/${practiceId}/reminders/${taskId}/`, 'patch', { is_completed: true });
            }

            setSaveResult('success');
            setTimeout(() => { onConfirmClose('Follow Up'); }, 1200);
        } catch (err) {
            console.error('[ResolvePanel] Follow up error:', err);
            setSaveResult('error');
        } finally {
            setIsSaving(false);
        }
    };

    // ── Close handler — different actions based on close reason ──
    const ARCHIVE_REASONS = ['Not Interested', 'Too Expensive', 'Gone Elsewhere'];
    // Map frontend labels to backend NOTE_CHOICES values (some don't match toUpperCase transform)
    const REASON_TO_NOTE_CHOICE = { 'Gone Elsewhere': 'ANOTHER_PRACTICE' };
    const FLAG_REASONS = ['Spam', 'Duplicate'];

    const handleClose = async () => {
        if (!closeReason || isSaving) return;
        setIsSaving(true);
        try {
            // Action based on close reason — note path handles count increment via contacted_via
            const contactedVia = calledConfirmed ? 'PHONE' : 'WHATSAPP';
            const treatmentField = treatmentId ? { treatment: treatmentId } : {};
            if (leadId && practiceId) {
                const noteReason = REASON_TO_NOTE_CHOICE[closeReason] || closeReason.toUpperCase().replace(/ /g, '_');
                if (ARCHIVE_REASONS.includes(closeReason)) {
                    await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/notes/`, 'post', {
                        note: noteReason, additional_notes: notes || `Archived: ${closeReason}`, lead: leadId,
                        contacted_via: contactedVia, ...treatmentField,
                    });
                    await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/archive/`, 'post', {
                        reason: noteReason, note: notes || `WhatsApp closed: ${closeReason}`,
                    });
                } else if (closeReason === 'Spam') {
                    await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/notes/`, 'post', {
                        note: 'SPAM', additional_notes: notes || 'Marked as spam from WhatsApp', lead: leadId,
                        contacted_via: contactedVia, ...treatmentField,
                    });
                } else if (closeReason === 'Duplicate') {
                    await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/notes/`, 'post', {
                        note: 'DUPLICATE', additional_notes: notes || 'Marked as duplicate from WhatsApp', lead: leadId,
                        contacted_via: contactedVia, ...treatmentField,
                    });
                } else {
                    await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/notes/`, 'post', {
                        note: 'OTHER', additional_notes: `WhatsApp closed: ${closeReason}${notes ? ` — ${notes}` : ''}`, lead: leadId,
                        contacted_via: contactedVia, ...treatmentField,
                    });
                }
            }

            // 3. Close WhatsApp thread
            const threadId = whatsAppThread?.id;
            if (threadId && leadId) {
                dispatch(closeWhatsappConversationWithReason(threadId, closeReason, leadId));
            }

            // 4. Complete To-Do task if from To-Do
            if (taskId && practiceId) {
                await callApi(`/api/v1/practices/${practiceId}/reminders/${taskId}/`, 'patch', { is_completed: true });
            }

            setSaveResult('success');
            setTimeout(() => { onConfirmClose(closeReason); }, 1200);
        } catch (err) {
            console.error('[ResolvePanel] Close error:', err);
            setSaveResult('error');
        } finally {
            setIsSaving(false);
        }
    };

    // ── Button config based on state ──
    const getFooterButton = () => {
        if (!selectedOutcome || selectedOutcome === 'APPOINTMENT_BOOKED') return null;

        if (saveResult === 'success') {
            let successLabel = 'Done ✓';
            if (selectedOutcome === 'FOLLOW_UP') {
                const presetLabel = reminderPreset === 'custom' ? moment(customDate).format('DD MMM, HH:mm') : (REMINDER_PRESETS.find(p => p.key === reminderPreset)?.label || '');
                successLabel = `Follow up · Reminder set${presetLabel ? ` — ${presetLabel}` : ''}`;
            } else if (ARCHIVE_REASONS.includes(closeReason)) {
                successLabel = `Archived · ${closeReason}`;
            } else if (closeReason === 'Spam') {
                successLabel = 'Marked as Spam';
            } else if (closeReason === 'Duplicate') {
                successLabel = 'Marked as Duplicate';
            } else {
                successLabel = `Closed · ${closeReason}`;
            }
            return {
                label: successLabel,
                icon: 'eva:checkmark-circle-2-fill',
                disabled: true,
                onClick: () => {},
                color: '#00AB55',
            };
        }

        if (selectedOutcome === 'FOLLOW_UP') {
            return {
                label: isSaving ? 'Saving...' : 'Save & Close',
                icon: isSaving ? null : 'eva:checkmark-circle-2-fill',
                disabled: !reminderPreset || (reminderPreset === 'custom' && !customDate) || calledConfirmed === null || isSaving,
                onClick: handleFollowUp,
                color: '#00AB55',
            };
        }

        if (selectedOutcome === 'OTHER') {
            /* eslint-disable no-nested-ternary */
            const closeLabel = isSaving ? 'Closing...' : ARCHIVE_REASONS.includes(closeReason) ? `Archive · ${closeReason}` : FLAG_REASONS.includes(closeReason) ? `Flag as ${closeReason}` : closeReason ? `Close · ${closeReason}` : 'Close & Resolve';
            return {
                label: closeLabel,
                icon: isSaving ? null : (ARCHIVE_REASONS.includes(closeReason) ? 'eva:archive-fill' : FLAG_REASONS.includes(closeReason) ? 'eva:flag-fill' : 'mdi:close-circle-outline'),
                disabled: !closeReason || calledConfirmed === null || isSaving,
                onClick: handleClose,
                color: ARCHIVE_REASONS.includes(closeReason) ? '#FFAB00' : FLAG_REASONS.includes(closeReason) ? '#FF5630' : '#637381',
            };
            /* eslint-enable no-nested-ternary */
        }

        return null;
    };

    const footerButton = getFooterButton();

    const outcomeLabels = {
        FOLLOW_UP: 'Follow Up',
        OTHER: 'Close',
    };

    // ── Appointment Booked → embedded BookingPanel ──
    if (selectedOutcome === 'APPOINTMENT_BOOKED') {
        return (
            <Box sx={{
                width: open ? 360 : 0, transition: 'width 300ms ease', overflow: 'hidden',
                flexShrink: 0, borderLeft: open ? '1px solid rgba(145,158,171,0.2)' : 'none', height: '100%',
            }}>
                <Box sx={{ width: 360, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, borderBottom: '1px solid rgba(145,158,171,0.12)', flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <IconButton size="small" onClick={() => setSelectedOutcome(null)} sx={{ width: 24, height: 24, mr: 0.25 }}>
                                <Icon icon="eva:arrow-back-fill" width={16} />
                            </IconButton>
                            <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#212B36' }}>Book</Typography>
                        </Box>
                        <IconButton onClick={onClose} size="small" sx={{ width: 28, height: 28 }}>
                            <Icon icon="eva:close-fill" width={18} />
                        </IconButton>
                    </Box>
                    <BookingPanel
                        open
                        onClose={() => { setSelectedOutcome(null); onClose(); }}
                        contactDetails={contactDetails}
                        practiceId={practiceId}
                        taskId={taskId}
                        onShareMessage={onShareMessage}
                        embedded
                    />
                </Box>
            </Box>
        );
    }

    // ── Main panel ──
    return (
        <Box sx={{
            width: open ? 360 : 0, transition: 'width 300ms ease', overflow: 'hidden',
            flexShrink: 0, borderLeft: open ? '1px solid rgba(145,158,171,0.2)' : 'none', height: '100%',
        }}>
            <Box sx={{ width: 360, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, borderBottom: '1px solid rgba(145,158,171,0.12)', flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {selectedOutcome && (
                            <IconButton size="small" onClick={() => { setSelectedOutcome(null); setSaveResult(null); }} sx={{ width: 24, height: 24, mr: 0.25 }}>
                                <Icon icon="eva:arrow-back-fill" width={16} />
                            </IconButton>
                        )}
                        <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#212B36' }}>
                            {selectedOutcome ? outcomeLabels[selectedOutcome] || 'Resolve' : 'Resolve'}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ width: 28, height: 28 }}>
                        <Icon icon="eva:close-fill" width={18} />
                    </IconButton>
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, py: 2 }}>

                    {/* Outcome selection */}
                    {!selectedOutcome && (
                    <>
                    <Typography sx={{ fontSize: '12px', color: '#919EAB', mb: 2, fontWeight: 500 }}>What happened?</Typography>
                    <Stack spacing={0.75}>
                        {[
                            { key: 'APPOINTMENT_BOOKED', icon: 'eva:calendar-fill', label: 'Booked', sub: 'Book an appointment', color: '#00AB55', bg: 'rgba(0,171,85,0.06)' },
                            { key: 'FOLLOW_UP', icon: 'eva:clock-fill', label: 'Follow up', sub: 'Set a reminder', color: '#078DEE', bg: 'rgba(7,141,238,0.06)' },
                            { key: 'OTHER', icon: 'eva:close-circle-fill', label: 'Close', sub: 'Close conversation', color: '#637381', bg: 'rgba(145,158,171,0.06)' },
                        ].map((opt) => (
                            <Box key={opt.key} onClick={() => setSelectedOutcome(opt.key)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                    px: 1.5, py: 1.25, borderRadius: '12px',
                                    cursor: 'pointer', transition: 'all 200ms ease',
                                    background: 'transparent',
                                    '&:hover': { background: opt.bg },
                                    '&:active': { transform: 'scale(0.98)' },
                                }}>
                                <Box sx={{
                                    width: 36, height: 36, borderRadius: '10px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: opt.bg, flexShrink: 0,
                                }}>
                                    <Icon icon={opt.icon} width={18} style={{ color: opt.color }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#212B36', lineHeight: 1.3 }}>{opt.label}</Typography>
                                    <Typography sx={{ fontSize: '11px', color: '#919EAB', fontWeight: 400, lineHeight: 1.3 }}>{opt.sub}</Typography>
                                </Box>
                                <Icon icon="eva:chevron-right-fill" width={16} style={{ color: '#C4CDD5', flexShrink: 0 }} />
                            </Box>
                        ))}
                    </Stack>
                    </>
                    )}

                    {/* Follow Up screen */}
                    {selectedOutcome === 'FOLLOW_UP' && (
                        <Stack spacing={1.5}>
                            {/* Did you also call? */}
                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Contact method</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Box onClick={() => setCalledConfirmed(true)} sx={{ ...compactChipStyle(calledConfirmed === true), fontSize: '11px', px: 1, py: 0.375 }}>
                                        <Icon icon="eva:phone-fill" width={14} style={{ marginRight: 4 }} /> Yes
                                    </Box>
                                    <Box onClick={() => setCalledConfirmed(false)} sx={{
                                        ...compactChipStyle(calledConfirmed === false), fontSize: '11px', px: 1, py: 0.375,
                                        ...(calledConfirmed === false && { borderColor: '#637381', color: '#fff', background: '#637381' }),
                                    }}>
                                        <Icon icon="ri:whatsapp-fill" width={14} style={{ marginRight: 4 }} /> WA only
                                    </Box>
                                </Box>
                            </Box>

                            {/* Call outcome — only when "Yes" */}
                            {calledConfirmed === true && (
                                <Box>
                                    <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Leave a note about the call attempt</Typography>
                                    <TextField size="small" fullWidth multiline minRows={2} value={callNote} onChange={(e) => setCallNote(e.target.value)}
                                        placeholder="Enter or select details about the call attempt"
                                        sx={{ mb: 0.75, '& .MuiOutlinedInput-root': { borderRadius: RADIUS, fontSize: '12px', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' } } }} />
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {['No answer', 'Left voicemail', 'Incorrect number', 'Call declined', 'Poor call quality', 'Reached different person', 'Busy line', 'Requested call-back'].map((opt) => (
                                            <Box key={opt} onClick={() => {
                                                const newSelected = selectedCallOptions.includes(opt) ? selectedCallOptions.filter(v => v !== opt) : [...selectedCallOptions, opt];
                                                setSelectedCallOptions(newSelected);
                                                setCallNote(newSelected.join(', '));
                                            }}
                                                sx={{ ...compactChipStyle(selectedCallOptions.includes(opt)), fontSize: '11px', px: 1, py: 0.375 }}>{opt}</Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {/* Reminder presets */}
                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Remind me</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {REMINDER_PRESETS.map((preset) => (
                                        <Box key={preset.key} onClick={() => setReminderPreset(preset.key)}
                                            sx={{
                                                ...compactChipStyle(reminderPreset === preset.key), fontSize: '11px', px: 1, py: 0.375,
                                                ...(reminderPreset === preset.key && preset.key === 'none' && { borderColor: '#637381', color: '#fff', background: '#637381' }),
                                            }}>
                                            {preset.label}
                                        </Box>
                                    ))}
                                </Box>
                                {reminderPreset === 'custom' && (
                                    <DateTimePicker
                                        value={customDate}
                                        onChange={(val) => setCustomDate(val)}
                                        disablePast
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                placeholder: 'Pick date & time',
                                                sx: {
                                                    mt: 0.75,
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: RADIUS, fontSize: '12px',
                                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00AB55', borderWidth: '1px' },
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                )}
                            </Box>

                            {/* Assignees */}
                            {reminderPreset && reminderPreset !== 'none' && (
                                <AssignedToLead label="Assignees" assignToState={assignToState} setAssignTo={setAssignToState} />
                            )}

                            {/* Notes */}
                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Notes</Typography>
                                <TextField size="small" fullWidth multiline minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                                    placeholder="e.g., Left voicemail, will call back next week"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: RADIUS, fontSize: '12px', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00AB55', borderWidth: '1px' } } }} />
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Icon icon="mdi:information-outline" width={12} style={{ color: '#919EAB' }} />
                                {/* eslint-disable no-nested-ternary */}
                                <Typography sx={{ fontSize: '10px', color: '#919EAB' }}>
                                    Will move to next Follow up
                                    {reminderPreset && reminderPreset !== 'none' && ` · Reminder: ${reminderPreset === 'custom' ? (customDate ? moment(customDate).format('DD MMM, HH:mm') : 'Custom') : REMINDER_PRESETS.find(p => p.key === reminderPreset)?.label}`}
                                </Typography>
                                {/* eslint-enable no-nested-ternary */}
                            </Box>
                        </Stack>
                    )}

                    {/* Close screen */}
                    {selectedOutcome === 'OTHER' && (
                        <Stack spacing={1.5}>
                            {/* Contact method */}
                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Contact method</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Box onClick={() => setCalledConfirmed(true)} sx={{ ...compactChipStyle(calledConfirmed === true), fontSize: '11px', px: 1, py: 0.375 }}>
                                        <Icon icon="eva:phone-fill" width={14} style={{ marginRight: 4 }} /> Yes
                                    </Box>
                                    <Box onClick={() => setCalledConfirmed(false)} sx={{
                                        ...compactChipStyle(calledConfirmed === false), fontSize: '11px', px: 1, py: 0.375,
                                        ...(calledConfirmed === false && { borderColor: '#637381', color: '#fff', background: '#637381' }),
                                    }}>
                                        <Icon icon="ri:whatsapp-fill" width={14} style={{ marginRight: 4 }} /> WA only
                                    </Box>
                                </Box>
                            </Box>

                            {/* Call outcome — only when "Yes" */}
                            {calledConfirmed === true && (
                                <Box>
                                    <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Leave a note about the call attempt</Typography>
                                    <TextField size="small" fullWidth multiline minRows={2} value={callNote} onChange={(e) => setCallNote(e.target.value)}
                                        placeholder="Enter or select details about the call attempt"
                                        sx={{ mb: 0.75, '& .MuiOutlinedInput-root': { borderRadius: RADIUS, fontSize: '12px', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' } } }} />
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {['No answer', 'Left voicemail', 'Incorrect number', 'Call declined', 'Poor call quality', 'Reached different person', 'Busy line', 'Requested call-back'].map((opt) => (
                                            <Box key={opt} onClick={() => {
                                                const newSelected = selectedCallOptions.includes(opt) ? selectedCallOptions.filter(v => v !== opt) : [...selectedCallOptions, opt];
                                                setSelectedCallOptions(newSelected);
                                                setCallNote(newSelected.join(', '));
                                            }}
                                                sx={{ ...compactChipStyle(selectedCallOptions.includes(opt)), fontSize: '11px', px: 1, py: 0.375 }}>{opt}</Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Archive</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {['Not Interested', 'Too Expensive', 'Gone Elsewhere'].map((r) => (
                                        <Box key={r} onClick={() => setCloseReason(r)}
                                            sx={{ ...compactChipStyle(closeReason === r), fontSize: '11px', px: 1, py: 0.375 }}>
                                            {r}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Flag</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {['Spam', 'Duplicate'].map((r) => (
                                        <Box key={r} onClick={() => setCloseReason(r)}
                                            sx={{ ...compactChipStyle(closeReason === r), fontSize: '11px', px: 1, py: 0.375 }}>
                                            {r}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Just close</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {['General Chat', 'Resolved'].map((r) => (
                                        <Box key={r} onClick={() => setCloseReason(r)}
                                            sx={{ ...compactChipStyle(closeReason === r), fontSize: '11px', px: 1, py: 0.375 }}>
                                            {r}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Notes</Typography>
                                <TextField size="small" fullWidth multiline minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Additional details..."
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: RADIUS, fontSize: '12px', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00AB55', borderWidth: '1px' } } }} />
                            </Box>
                        </Stack>
                    )}
                </Box>

                {/* Footer — dynamic button */}
                {footerButton && (
                    <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid rgba(145,158,171,0.12)', flexShrink: 0 }}>
                        {/* eslint-disable no-nested-ternary */}
                        <Button variant="contained" fullWidth onClick={footerButton.onClick}
                            disabled={footerButton.disabled}
                            startIcon={footerButton.icon ? (isSaving ? <CircularProgress size={14} color="inherit" /> : <Icon icon={footerButton.icon} width={16} />) : <CircularProgress size={14} color="inherit" />}
                            sx={{
                                background: saveResult === 'success' ? '#00AB55' : footerButton.color,
                                '&:hover': { background: saveResult === 'success' ? '#00AB55' : (footerButton.color === '#FF5630' ? '#B71D18' : '#007B55') },
                                '&.Mui-disabled': saveResult === 'success'
                                    ? { background: '#00AB55', color: '#fff', opacity: 0.9 }
                                    : { background: '#f4f6f8', color: '#C4CDD5' },
                                textTransform: 'none', fontWeight: 600, fontSize: '13px', borderRadius: RADIUS, py: 1, boxShadow: 'none',
                            }}>
                            {footerButton.label}
                        </Button>
                        {/* eslint-enable no-nested-ternary */}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

ResolvePanel.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirmClose: PropTypes.func.isRequired,
    contactDetails: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onShareMessage: PropTypes.func,
};
